import { Alert, Badge, Button, Card, CardBody } from '@foundation/ui'
import { Link, createFileRoute, redirect } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import styles from './dashboard.module.css'
import { AppLayout } from '@/components/AppLayout'
import { Skeleton } from '@/components/Skeleton'
import { api, type DashboardStats, type Testimonial, type TestimonialStatus } from '@/lib/api'
import { getAuthState, isAuthenticated, type User } from '@/lib/auth-store'
import { formatRelativeTime, truncateText } from '@/lib/format'

export const Route = createFileRoute('/dashboard')({
  beforeLoad: () => {
    if (!isAuthenticated()) {
      throw redirect({ to: '/login' })
    }
  },
  component: DashboardPage,
})

const STATUS_VARIANT: Record<TestimonialStatus, 'warning' | 'success' | 'danger'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
}

function StarRating({ rating }: { rating: number | null }) {
  if (rating === null) return null
  return (
    <span className={styles.testimonialRating}>
      {[1, 2, 3, 4, 5].map((star) => (
        <span key={star} className={star <= rating ? styles.starFilled : styles.starEmpty}>
          &#9733;
        </span>
      ))}
    </span>
  )
}

function DashboardPage() {
  const auth = getAuthState()
  const [user] = useState<User | null>(auth.user)
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [testimonials, setTestimonials] = useState<Testimonial[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const [statsResponse, testimonialsResponse] = await Promise.all([
          api.getDashboardStats(),
          api.listTestimonials({ limit: 5 }),
        ])
        setStats(statsResponse.data)
        setTestimonials(testimonialsResponse.data)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load dashboard data')
      } finally {
        setIsLoading(false)
      }
    }
    void fetchData()
  }, [])

  if (isLoading) {
    return (
      <AppLayout title="Dashboard">
        {/* Welcome Section */}
        <Card className={styles.welcomeCard}>
          <CardBody>
            <h2 className={styles.welcomeTitle}>
              Welcome back, {user?.name ?? user?.email?.split('@')[0]}!
            </h2>
            <p className={styles.welcomeSubtitle}>Here&apos;s an overview of your social proof.</p>
          </CardBody>
        </Card>

        {/* Stats Grid Loading */}
        <div className={styles.statsGrid}>
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className={styles.statCard}>
              <CardBody>
                <Skeleton width="3rem" height="2rem" />
                <Skeleton width="80%" height="1rem" />
              </CardBody>
            </Card>
          ))}
        </div>

        {/* Recent Section Loading */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>Recent Testimonials</h3>
          </div>
          <div className={styles.testimonialsList}>
            {[1, 2, 3].map((i) => (
              <Card key={i} className={styles.testimonialCard}>
                <CardBody>
                  <div className={styles.testimonialHeader}>
                    <div className={styles.testimonialInfo}>
                      <Skeleton width="60%" height="1.25rem" />
                      <Skeleton width="30%" height="1rem" />
                    </div>
                    <Skeleton width="80px" height="24px" />
                  </div>
                  <div className={styles.testimonialContent}>
                    <Skeleton width="100%" height="1rem" />
                  </div>
                </CardBody>
              </Card>
            ))}
          </div>
        </div>
      </AppLayout>
    )
  }

  if (error) {
    return (
      <AppLayout title="Dashboard">
        <Card className={styles.welcomeCard}>
          <CardBody>
            <h2 className={styles.welcomeTitle}>
              Welcome back, {user?.name ?? user?.email?.split('@')[0]}!
            </h2>
          </CardBody>
        </Card>
        <Alert variant="danger">{error}</Alert>
      </AppLayout>
    )
  }

  const displayStats = stats ?? {
    totalTestimonials: 0,
    pendingTestimonials: 0,
    widgetImpressions: 0,
    collectionPageVisits: 0,
    approvedTestimonials: 0,
    totalWidgets: 0,
    totalCollectionPages: 0,
  }

  return (
    <AppLayout title="Dashboard">
      {/* Welcome Section */}
      <Card className={styles.welcomeCard}>
        <CardBody>
          <h2 className={styles.welcomeTitle}>
            Welcome back, {user?.name ?? user?.email?.split('@')[0]}!
          </h2>
          <p className={styles.welcomeSubtitle}>Here&apos;s an overview of your social proof.</p>
        </CardBody>
      </Card>

      {/* Stats Grid */}
      <div className={styles.statsGrid}>
        <Card className={styles.statCard}>
          <CardBody>
            <div className={styles.statValue}>{displayStats.totalTestimonials}</div>
            <div className={styles.statLabel}>Total Testimonials</div>
          </CardBody>
        </Card>
        <Card className={`${styles.statCard} ${styles.statPending}`}>
          <CardBody>
            <div className={styles.statValue}>{displayStats.pendingTestimonials}</div>
            <div className={styles.statLabel}>Pending Review</div>
          </CardBody>
        </Card>
        <Card className={styles.statCard}>
          <CardBody>
            <div className={styles.statValue}>{displayStats.widgetImpressions}</div>
            <div className={styles.statLabel}>Widget Impressions</div>
          </CardBody>
        </Card>
        <Card className={styles.statCard}>
          <CardBody>
            <div className={styles.statValue}>{displayStats.collectionPageVisits}</div>
            <div className={styles.statLabel}>Collection Page Visits</div>
          </CardBody>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Quick Actions</h3>
        </div>
        <div className={styles.quickActions}>
          <Link to="/collection-pages">
            <Button variant="primary">Create Collection Page</Button>
          </Link>
          <Link to="/widgets">
            <Button variant="secondary">Create Widget</Button>
          </Link>
          {displayStats.pendingTestimonials > 0 && (
            <Link to="/testimonials">
              <Button variant="secondary">
                Review Pending ({displayStats.pendingTestimonials})
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Empty State or Recent Testimonials */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Recent Testimonials</h3>
          {displayStats.totalTestimonials > 0 && (
            <Link to="/testimonials" className={styles.viewAll}>
              View all ({displayStats.totalTestimonials})
            </Link>
          )}
        </div>

        {displayStats.totalTestimonials === 0 ? (
          <Card className={styles.emptyState}>
            <CardBody>
              <div className={styles.emptyIcon}>&#128172;</div>
              <h3 className={styles.emptyTitle}>No testimonials yet</h3>
              <p className={styles.emptyDescription}>
                Create a collection page and share the link with your customers to start collecting
                testimonials.
              </p>
              <Link to="/collection-pages">
                <Button variant="primary">Create your first collection page</Button>
              </Link>
            </CardBody>
          </Card>
        ) : (
          <div className={styles.testimonialsList}>
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className={styles.testimonialCard}>
                <CardBody>
                  <div className={styles.testimonialHeader}>
                    <div className={styles.testimonialInfo}>
                      <span className={styles.testimonialAuthor}>{testimonial.authorName}</span>
                      {(testimonial.authorCompany || testimonial.authorRole) && (
                        <span className={styles.testimonialMeta}>
                          {testimonial.authorRole && testimonial.authorCompany
                            ? `${testimonial.authorRole} at ${testimonial.authorCompany}`
                            : (testimonial.authorRole ?? testimonial.authorCompany)}
                        </span>
                      )}
                    </div>
                    <div className={styles.testimonialHeaderRight}>
                      <Badge variant={STATUS_VARIANT[testimonial.status]}>
                        <span className={styles.testimonialStatus}>
                          {testimonial.status.charAt(0).toUpperCase() + testimonial.status.slice(1)}
                        </span>
                      </Badge>
                    </div>
                  </div>
                  <StarRating rating={testimonial.rating} />
                  <p className={styles.testimonialText}>{truncateText(testimonial.content, 150)}</p>
                  <span className={styles.testimonialDate}>
                    {formatRelativeTime(testimonial.createdAt)}
                  </span>
                </CardBody>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Tips Card */}
      <Card className={styles.tipsCard}>
        <CardBody>
          <h3 className={styles.tipsTitle}>Getting Started</h3>
          <ul className={styles.tipsList}>
            <li>Create a collection page with your branding and custom questions</li>
            <li>Share the link with customers via email, social media, or QR code</li>
            <li>Review and approve incoming testimonials from the Testimonials page</li>
            <li>Create widgets to embed approved testimonials on your website</li>
          </ul>
        </CardBody>
      </Card>
    </AppLayout>
  )
}
